import { StateMachineConfig } from "./types";

export class FSM<T extends string, Data = any> {
  private readonly _initialState: T;
  private _currentState: T;

  constructor(private config: StateMachineConfig<T, Data>, initialState: T) {
    this._currentState = initialState;
    this._initialState = initialState;
  }

  /**
   * Returns the state with which the FSM was initialized.
   */
  get initialState(): T {
    return this._initialState;
  }

  /**
   * Returns the current state.
   */
  get currentState(): T {
    return this._currentState;
  }

  /**
   * Returns true if the machine can transition from its current state to the given state.
   */
  canTransitionTo(state: T): boolean {
    return -1 < this.config.transitions[this._currentState].to.indexOf(state);
  }

  /**
   * Attempts to transition from the current state to the given state. If this transition is not allowed
   * per the config, then an error will be logged.
   */
  async transitionTo(state: T, data: Data) {
    if (this.canTransitionTo(state)) {
      // If the onTransitionStart callback is defined, invoke it. If it returns false,
      // then the transition will be cancelled.
      if (typeof this.config.onTransitionStart === "function") {
        const canTransition = await awaitPromise(
          this.config.onTransitionStart(this._currentState, state, data)
        );
        if (canTransition === false) {
          return;
        } else if (typeof canTransition === "string") {
          await this.onError(this._currentState, state, canTransition);
          return;
        }
      }
      const fromState = this._currentState;
      // All is well, so transition to the new state.
      this._currentState = state;
    } else {
      return this.onError(this._currentState, state);
    }
  }

  private async onError(fromState: T, toState: T, message?: string) {
    if (typeof this.config.onError === "function") {
      await awaitPromise(this.config.onError(fromState, toState, message));
    }
  }
}

/**
 * Converts a value that may be wrapped into a Promise
 * value.
 */
export async function awaitPromise<T>(value: T | Promise<T>): Promise<T> {
  let result = await value;
  return result;
}
